// hooks/features/invitations/useInvitations.ts
import { useState, useCallback } from "react";
import useToken from "../../../hooks/features/auth/useToken";
import { Invitation } from "../../../types/features/Invitation";
import { invitationService } from "../../../services/invitationService";

export default function useInvitations() {
  const { token } = useToken();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const getInvitations = useCallback(async (
    page: number = 1, 
    perPage: number = 20,
    status?: string,
    search?: string
  ) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await invitationService.getInvitations(token, page, perPage, status, search);
      setInvitations(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des invitations");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createInvitation = useCallback(async (
    data: { email: string; departement?: string; responsabilities?: string[] }
  ): Promise<Invitation | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await invitationService.createInvitation(token, data);
      setInvitations((prev) => [response.invitation, ...prev]);
      return response.invitation;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la création de l'invitation";
      setError(errorMessage);
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const resendEmail = useCallback(async (id: string): Promise<Invitation | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await invitationService.resendEmail(token, id);
      // Mettre à jour l'invitation dans la liste
      setInvitations((prev) => 
        prev.map((inv) => inv.id === id ? response.invitation : inv)
      );
      if (selectedInvitation?.id === id) {
        setSelectedInvitation(response.invitation);
      }
      return response.invitation;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors du renvoi de l'email";
      setError(errorMessage);
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, selectedInvitation]);

  const regenerateLink = useCallback(async (id: string): Promise<Invitation | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await invitationService.regenerateLink(token, id);
      setInvitations((prev) => 
        prev.map((inv) => inv.id === id ? response.invitation : inv)
      );
      if (selectedInvitation?.id === id) {
        setSelectedInvitation(response.invitation);
      }
      return response.invitation;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la régénération du lien";
      setError(errorMessage);
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, selectedInvitation]);

  const deleteInvitation = useCallback(async (id: string): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await invitationService.deleteInvitation(token, id);
      setInvitations((prev) => prev.filter((inv) => inv.id !== id));
      if (selectedInvitation?.id === id) setSelectedInvitation(null);
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la suppression de l'invitation";
      setError(errorMessage);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token, selectedInvitation]);

  return {
    invitations,
    selectedInvitation,
    setSelectedInvitation,
    loading,
    error,
    setError,
    pagination,
    getInvitations,
    createInvitation,
    resendEmail,
    regenerateLink,
    deleteInvitation,
  };
}